import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import {
    ArrowLeft,
    Package,
    CreditCard,
    User,
    MapPin,
    Book,
    PackageCheck,
    Clock,
} from "lucide-react";

import adminApi from "@/services/api/admin";
import type { Order, Address } from "@/services/types/domain";
import { Badge } from "@/components/ui/Badge";
import Button from "@/components/ui/Button";
import {
    formatPrice,
    tOrderStatus,
    tPaymentStatus,
    orderVariant,
    paymentVariant,
    tRefundStatus,
    refundVariant,
    parseAddress,
} from "@/utils/formatters";

const toNum = (v: any, d = 0) => {
    if (v === null || v === undefined || v === "") return d;
    const n = typeof v === "string" ? parseFloat(v) : Number(v);
    return Number.isFinite(n) ? n : d;
};

// Acepta string u objeto y devuelve Address normalizada
const normalizeAddress = (addr: any): Address | null => {
    if (!addr) return null;
    if (typeof addr === "string") {
        try {
            return parseAddress(addr) as Address;
        } catch {
            return null;
        }
    }
    if (typeof addr === "object") {
        // asumimos shape correcto del backend
        return addr as Address;
    }
    return null;
};

const AdminOrderDetailPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();

    const [order, setOrder] = useState<Order & {
        tracking?: { status: string; notes?: string | null; timestamp: string }[];
    } | null>(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState<string | null>(null);

    const currency = order?.currency || "PEN";

    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                setLoading(true);
                setErr(null);
                const data = await adminApi.getOrderById(Number(id));
                if (!mounted) return;
                // El servicio ya adapta ApiResponse → data
                setOrder(data as any);
            } catch (e: any) {
                const msg = e?.response?.data?.message || e?.message || "No se pudo cargar la orden";
                setErr(msg);
                toast.error(msg);
            } finally {
                if (mounted) setLoading(false);
            }
        })();
        return () => { mounted = false; };
    }, [id]);

    const totals = useMemo(() => {
        if (!order) return { subtotal: 0, tax: 0, shipping: 0, discount: 0, total: 0 };
        return {
            subtotal: toNum(order.subtotal),
            tax: toNum(order.tax_amount),
            shipping: toNum(order.shipping_amount),
            discount: toNum(order.discount_amount),
            total: toNum(order.total_amount),
        };
    }, [order]);

    const shippingAddr = useMemo(() => normalizeAddress(order?.shipping_address), [order]);
    const billingAddr = useMemo(() => normalizeAddress(order?.billing_address), [order]);

    if (loading) {
        return (
            <div className="min-h-screen grid place-items-center bg-gray-50">
                <div className="text-gray-500 animate-pulse">Cargando…</div>
            </div>
        );
    }
    if (err || !order) {
        return (
            <div className="min-h-screen bg-gray-50 px-4 py-6">
                <div className="mx-auto max-w-5xl">
                    <Link to="/admin/orders" className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-4">
                        <ArrowLeft className="w-4 h-4 mr-1" /> Volver
                    </Link>
                    <div className="rounded-2xl border bg-white p-6 text-red-600">{err ?? "No se encontró la orden"}</div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 px-4 py-6">
            <div className="mx-auto max-w-6xl space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <Link to="/admin/orders" className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900">
                        <ArrowLeft className="w-4 h-4 mr-1" /> Volver a órdenes
                    </Link>
                    <div className="flex flex-wrap gap-2">
                        <Badge variant={orderVariant(order.status)} icon={<Package className="w-3.5 h-3.5" />}>
                            {tOrderStatus(order.status)}
                        </Badge>
                        <Badge variant={paymentVariant(order.payment_status)} icon={<CreditCard className="w-3.5 h-3.5" />}>
                            {tPaymentStatus(order.payment_status)}
                        </Badge>
                        {typeof order.refund_request_status !== "undefined" && order.refund_request_status && (
                            <Badge variant={refundVariant(order.refund_request_status)}>
                                {tRefundStatus(order.refund_request_status)}
                            </Badge>
                        )}
                    </div>
                </div>

                {/* Identificación */}
                <div className="rounded-2xl border bg-white p-5 shadow-sm">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                        <div>
                            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
                                Pedido {order.order_number || `#${order.id}`}
                            </h1>
                            <div className="text-sm text-gray-500">
                                Creado: {order.created_at ? new Date(order.created_at).toLocaleString() : "—"}
                            </div>
                        </div>
                        <div className="text-right">
                            <div className="text-sm text-gray-600">Total</div>
                            <div className="text-xl font-semibold text-gray-900">
                                {formatPrice(totals.total, currency)}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Columna principal: Items + Totales + Tracking */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* Items */}
                        <div className="rounded-2xl border bg-white p-5 shadow-sm">
                            <div className="mb-3 flex items-center justify-between">
                                <div className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                    <PackageCheck className="w-5 h-5" /> Artículos ({order.items?.length ?? 0})
                                </div>
                            </div>

                            <div className="hidden md:grid grid-cols-12 gap-2 text-xs font-semibold text-gray-500 px-2 pb-2">
                                <div className="col-span-6">Producto</div>
                                <div className="col-span-2 text-right">Precio</div>
                                <div className="col-span-2 text-right">Cantidad</div>
                                <div className="col-span-2 text-right">Total</div>
                            </div>

                            <div className="divide-y">
                                {order.items?.map((it) => (
                                    <div key={it.id} className="py-3 px-2 grid grid-cols-1 gap-2 md:grid-cols-12 md:items-center">
                                        <div className="md:col-span-6">
                                            <div className="font-medium text-gray-900">{it.product_name}</div>
                                            <div className="text-xs text-gray-500">SKU: {it.product_sku}</div>
                                            {(it as any).store_name && (
                                                <div className="text-xs text-gray-500">Tienda: {(it as any).store_name}</div>
                                            )}
                                        </div>
                                        <div className="md:col-span-2 text-right text-sm text-gray-700">
                                            {formatPrice(toNum(it.price), currency)}
                                        </div>
                                        <div className="md:col-span-2 text-right text-sm text-gray-700">
                                            {it.quantity}
                                        </div>
                                        <div className="md:col-span-2 text-right text-sm font-semibold text-gray-900">
                                            {formatPrice(toNum(it.total), currency)}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Totales */}
                            <div className="mt-4 flex flex-col items-end gap-1 text-sm">
                                <div className="flex w-full md:w-auto justify-between md:justify-end gap-6">
                                    <span className="text-gray-600">Subtotal</span>
                                    <span className="font-medium">{formatPrice(totals.subtotal, currency)}</span>
                                </div>
                                <div className="flex w-full md:w-auto justify-between md:justify-end gap-6">
                                    <span className="text-gray-600">Impuestos</span>
                                    <span className="font-medium">{formatPrice(totals.tax, currency)}</span>
                                </div>
                                <div className="flex w-full md:w-auto justify-between md:justify-end gap-6">
                                    <span className="text-gray-600">Envío</span>
                                    <span className="font-medium">{formatPrice(totals.shipping, currency)}</span>
                                </div>
                                <div className="flex w-full md:w-auto justify-between md:justify-end gap-6">
                                    <span className="text-gray-600">Descuento</span>
                                    <span className="font-medium">- {formatPrice(totals.discount, currency)}</span>
                                </div>
                                <div className="flex w-full md:w-auto justify-between md:justify-end gap-6 text-base">
                                    <span className="text-gray-900 font-semibold">Total</span>
                                    <span className="font-bold">{formatPrice(totals.total, currency)}</span>
                                </div>
                            </div>
                        </div>

                        {/* Timeline / Tracking */}
                        {!!order.tracking?.length && (
                            <div className="rounded-2xl border bg-white p-5 shadow-sm">
                                <div className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                                    <Clock className="w-5 h-5" /> Historial del pedido
                                </div>
                                <div className="space-y-3">
                                    {order.tracking!.map((t, i) => (
                                        <div key={i} className="flex items-start justify-between gap-3">
                                            <div>
                                                <div className="font-medium text-gray-900">
                                                    {/* Si coincide con estados de pedido, traducimos; si no, mostramos tal cual */}
                                                    {["pending", "processing", "shipped", "delivered", "cancelled", "refunded"].includes(t.status)
                                                        ? tOrderStatus(t.status as Order["status"])
                                                        : t.status}
                                                </div>
                                                {t.notes && <div className="text-sm text-gray-600">{t.notes}</div>}
                                            </div>
                                            <div className="text-xs text-gray-500">
                                                {new Date(t.timestamp).toLocaleString()}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Columna lateral: Cliente, Direcciones, Pago, Notas */}
                    <div className="space-y-6">
                        {/* Cliente */}
                        <div className="rounded-2xl border bg-white p-5 shadow-sm">
                            <div className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                                <User className="w-5 h-5" /> Cliente
                            </div>
                            <div className="text-sm text-gray-700">
                                <div className="font-medium">
                                    {[order.first_name, order.last_name].filter(Boolean).join(" ") || order.email || "—"}
                                </div>
                                {order.email && <div className="text-gray-500">{order.email}</div>}
                            </div>
                        </div>

                        {/* Envío */}
                        <div className="rounded-2xl border bg-white p-5 shadow-sm">
                            <div className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                                <MapPin className="w-5 h-5" /> Envío
                            </div>
                            <div className="text-sm text-gray-700">
                                {shippingAddr ? (
                                    <>
                                        <div className="font-medium">{shippingAddr.recipient_name}</div>
                                        <div>
                                            {shippingAddr.address_line_1}
                                            {shippingAddr.address_line_2 ? `, ${shippingAddr.address_line_2}` : ""}
                                        </div>
                                        <div>
                                            {shippingAddr.city}, {shippingAddr.state} {shippingAddr.postal_code}
                                        </div>
                                        <div>{shippingAddr.country}</div>
                                        {shippingAddr.phone && <div className="text-gray-500 mt-1">{shippingAddr.phone}</div>}
                                    </>
                                ) : (
                                    <div className="text-gray-500">Sin dirección</div>
                                )}
                            </div>
                        </div>

                        {/* Facturación */}
                        <div className="rounded-2xl border bg-white p-5 shadow-sm">
                            <div className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                                <MapPin className="w-5 h-5" /> Facturación
                            </div>
                            <div className="text-sm text-gray-700">
                                {billingAddr ? (
                                    <>
                                        <div className="font-medium">{billingAddr.recipient_name}</div>
                                        <div>
                                            {billingAddr.address_line_1}
                                            {billingAddr.address_line_2 ? `, ${billingAddr.address_line_2}` : ""}
                                        </div>
                                        <div>
                                            {billingAddr.city}, {billingAddr.state} {billingAddr.postal_code}
                                        </div>
                                        <div>{billingAddr.country}</div>
                                        {billingAddr.phone && <div className="text-gray-500 mt-1">{billingAddr.phone}</div>}
                                    </>
                                ) : (
                                    <div className="text-gray-500">Sin dirección</div>
                                )}
                            </div>
                        </div>

                        {/* Pago */}
                        <div className="rounded-2xl border bg-white p-5 shadow-sm">
                            <div className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                                <CreditCard className="w-5 h-5" /> Pago
                            </div>
                            <div className="flex flex-wrap gap-2 items-center text-sm">
                                <span className="text-gray-700">Estado:</span>
                                <Badge variant={paymentVariant(order.payment_status)}>
                                    {tPaymentStatus(order.payment_status)}
                                </Badge>
                            </div>
                        </div>

                        {/* Notas */}
                        <div className="rounded-2xl border bg-white p-5 shadow-sm">
                            <div className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                                <Book className="w-5 h-5" /> Notas
                            </div>
                            <div className="text-sm text-gray-700">
                                {order.notes ? (
                                    <div className="rounded-md border bg-gray-50 p-3">{order.notes}</div>
                                ) : (
                                    <span className="text-gray-500">Sin notas</span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer simple */}
                <div className="flex items-center justify-end">
                    <Button asChild variant="outline">
                        <Link to="/admin/orders">
                            <ArrowLeft className="w-4 h-4 mr-2" />
                            Volver a órdenes
                        </Link>
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default AdminOrderDetailPage;
